import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNotifications } from '../notifications/NotificationSystem';
import ApiService from '../../services/api';

const ApprovalWorkflow = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [pendingServices, setPendingServices] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const { showSnackbar } = useNotifications();

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    try {
      const [uRes, jRes, sRes] = await Promise.all([
        ApiService.request('/approval/pending/users'),
        ApiService.request('/approval/pending/jobs'),
        ApiService.request('/approval/pending/services')
      ]);

      setPendingUsers(uRes.users || []);
      setPendingJobs(jRes.jobs || []);
      setPendingServices(sRes.services || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      showSnackbar('Failed to fetch pending approvals', 'error');
    } finally {
      // no-op
    }
  }, [showSnackbar]);

  // Load pending items
  // eslint-disable-next-line no-use-before-define
  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  const handleAction = (item, action) => {
    setSelectedItem(item);
    setActionType(action);
    setDialogOpen(true);
  };

  // Handle approval/rejection
  const handleApproval = async (type, id, status, comment = '') => {
    try {
      setProcessing(true);
      
      switch (type) {
        case 'user':
          if (status === 'approved') {
            await ApiService.request(`/approval/users/${id}/approve`, {
              method: 'PUT',
              body: JSON.stringify({ reviewNotes: comment })
            });
          } else {
            await ApiService.request(`/approval/users/${id}/reject`, {
              method: 'PUT',
              body: JSON.stringify({ reviewNotes: comment })
            });
          }
          break;
        case 'job':
          if (status === 'approved') {
            await ApiService.approveJob(id, comment);
          } else {
            await ApiService.rejectJob(id, comment);
          }
          break;
        case 'service':
          if (status === 'approved') {
            await ApiService.approveService(id, { reviewNotes: comment });
          } else {
            await ApiService.rejectService(id, { reviewNotes: comment });
          }
          break;
        default:
          throw new Error('Invalid approval type');
      }

      showSnackbar(
        `${type.charAt(0).toUpperCase() + type.slice(1)} ${status === 'approved' ? 'approved' : 'rejected'} successfully`,
        'success'
      );
      
      // Refresh the data
      await fetchPendingApprovals();
    } catch (error) {
      console.error(`Error ${status === 'approved' ? 'approving' : 'rejecting'} ${type}:`, error);
      showSnackbar(`Failed to ${status === 'approved' ? 'approve' : 'reject'} ${type}`, 'error');
    } finally {
      setProcessing(false);
      setDialogOpen(false);
      setSelectedItem(null);
      setComment('');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
    setActionType('');
    setComment('');
  };

  const handleConfirmAction = async () => {
    if (!selectedItem) return;
    
    const status = actionType === 'approve' ? 'approved' : 'rejected';
    await handleApproval(selectedItem.type, selectedItem.id, status, comment);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderUserApprovals = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Registration Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
           {pendingUsers.map((user) => (
             <TableRow key={user.id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={user.profilePicture}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Chip 
                  label={user.userType === 'serviceprovider' ? 'employer' : user.userType} 
                  size="small" 
                  icon={user.userType === 'employer' || user.userType === 'serviceprovider' ? <BusinessIcon /> : <PersonIcon />}
                />
              </TableCell>
              <TableCell>{formatDate(user.createdAt)}</TableCell>
              <TableCell>
                <Chip 
                  label={user.status} 
                  color={getStatusColor(user.status)} 
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleAction({...user, type: 'user'}, 'view')}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Approve">
                    <IconButton 
                      size="small" 
                      color="success"
                      onClick={() => handleAction({...user, type: 'user'}, 'approve')}
                    >
                      <ApproveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleAction({...user, type: 'user'}, 'reject')}
                    >
                      <RejectIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderJobApprovals = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Job Title</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Posted Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
           {pendingJobs.map((job) => (
             <TableRow key={job.id}>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {job.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {typeof job.location === 'object' 
                      ? `${job.location.city || ''}, ${job.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') 
                      : job.location || 'N/A'} • {job.employmentType}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{job.companyName}</TableCell>
              <TableCell>{formatDate(job.createdAt)}</TableCell>
              <TableCell>
                <Chip 
                  label={job.status} 
                  color={getStatusColor(job.status)} 
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleAction({...job, type: 'job'}, 'view')}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Approve">
                    <IconButton 
                      size="small" 
                      color="success"
                      onClick={() => handleAction({...job, type: 'job'}, 'approve')}
                    >
                      <ApproveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleAction({...job, type: 'job'}, 'reject')}
                    >
                      <RejectIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderServiceApprovals = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Service</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Posted Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
           {pendingServices.map((service) => (
             <TableRow key={service.id}>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {service.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {typeof service.location === 'object' 
                      ? `${service.location.city || ''}, ${service.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') 
                      : service.location || 'N/A'} • {service.price}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{service.providerName}</TableCell>
              <TableCell>
                <Chip label={service.category} size="small" variant="outlined" />
              </TableCell>
              <TableCell>{formatDate(service.createdAt)}</TableCell>
              <TableCell>
                <Chip 
                  label={service.status} 
                  color={getStatusColor(service.status)} 
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleAction({...service, type: 'service'}, 'view')}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Approve">
                    <IconButton 
                      size="small" 
                      color="success"
                      onClick={() => handleAction({...service, type: 'service'}, 'approve')}
                    >
                      <ApproveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleAction({...service, type: 'service'}, 'reject')}
                    >
                      <RejectIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const tabContent = [
    { label: 'User Registrations', content: renderUserApprovals(), count: pendingUsers.length },
    { label: 'Job Postings', content: renderJobApprovals(), count: pendingJobs.length },
    { label: 'Service Listings', content: renderServiceApprovals(), count: pendingServices.length }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Approval Workflow
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Review and approve pending registrations, job postings, and service listings. 
        Users will be notified of your decisions automatically.
      </Alert>

      <Card>
        <CardContent>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            {tabContent.map((tab, index) => (
              <Tab 
                key={index}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tab.label}
                    {tab.count > 0 && (
                      <Chip size="small" label={tab.count} color="warning" />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>

          {tabContent[activeTab].content}
        </CardContent>
      </Card>

      {/* Approval/Rejection Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Item' : 'Reject Item'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionType === 'approve' 
              ? 'Are you sure you want to approve this item?' 
              : 'Please provide a reason for rejection:'
            }
          </Typography>
          
          <TextField
             fullWidth
             multiline
             rows={4}
             label={actionType === 'approve' ? 'Comments (Optional)' : 'Rejection Reason'}
             value={comment}
             onChange={(e) => setComment(e.target.value)}
             required={actionType === 'reject'}
           />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
             onClick={handleConfirmAction}
             variant="contained"
             color={actionType === 'approve' ? 'success' : 'error'}
             disabled={processing || (actionType === 'reject' && !comment.trim())}
           >
             {processing ? 'Processing...' : (actionType === 'approve' ? 'Approve' : 'Reject')}
           </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalWorkflow;